import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowRight, TrendingUp } from 'lucide-react';

/**
 * IncomeBand — slides in 400ms after scheme results load.
 * Shows income opportunities matching the user's detected profile.
 * Props:
 *  - bandData: { profile, header, opportunities[], colors, cta_link }
 *  - onDismiss: () => void
 *  - onDismissPermanent: () => void
 */
export default function IncomeBand({ bandData, onDismiss, onDismissPermanent }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [showDismissMenu, setShowDismissMenu] = useState(false);

  // Slide in after 400ms delay
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(timer);
  }, []);

  if (!bandData) return null;

  const { header, opportunities = [], colors = {}, cta_link } = bandData;

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 300); // wait for exit animation
  };

  const handleDismissPermanent = () => {
    setVisible(false);
    setShowDismissMenu(false);
    setTimeout(onDismissPermanent, 300);
  };

  return (
    <div
      data-testid="income-band"
      className="transition-all duration-300 ease-out overflow-hidden"
      style={{
        maxHeight: visible ? '400px' : '0px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
      }}
    >
      <div
        className="relative rounded-xl border p-4 sm:p-5 mt-4"
        style={{
          backgroundColor: colors.bg || '#EAF3DE',
          borderColor: colors.border || '#C0DD97',
        }}
      >
        {/* Smart Tip badge + dismiss */}
        <div className="flex items-center justify-between mb-3">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
            style={{ backgroundColor: colors.border || '#C0DD97', color: '#fff' }}
            data-testid="smart-tip-badge"
          >
            <TrendingUp className="w-3 h-3" />
            Smart Tip
          </div>
          <div className="relative">
            <button
              data-testid="income-band-dismiss"
              onClick={() => setShowDismissMenu(!showDismissMenu)}
              className="p-1 rounded-full hover:bg-black/5 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" style={{ color: colors.text || '#4A7C10' }} />
            </button>
            {showDismissMenu && (
              <div className="absolute right-0 top-7 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 w-44">
                <button
                  data-testid="dismiss-once"
                  onClick={handleDismiss}
                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                >
                  Hide this tip
                </button>
                <button
                  data-testid="dismiss-permanent"
                  onClick={handleDismissPermanent}
                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                >
                  Don't show tips
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Header */}
        <p
          className="text-xs font-medium mb-3 leading-relaxed"
          style={{ color: colors.text || '#4A7C10' }}
          data-testid="income-band-header"
        >
          {header}
        </p>

        {/* Income opportunity rows */}
        <div className="space-y-2.5">
          {opportunities.map((opp, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`income-opp-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-xs text-gray-700 truncate">{opp.name}</span>
                  <span
                    className="text-xs font-bold flex-shrink-0 ml-2"
                    style={{ color: colors.text || '#4A7C10' }}
                  >
                    {opp.value}
                  </span>
                </div>
                <div className="w-full bg-white/60 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${opp.bar_pct || 50}%`,
                      backgroundColor: colors.border || '#C0DD97',
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          data-testid="income-band-cta"
          onClick={() => navigate(cta_link || '/income-intelligence')}
          className="mt-4 flex items-center gap-1.5 text-xs font-semibold transition-opacity hover:opacity-80"
          style={{ color: colors.text || '#4A7C10' }}
        >
          See full income plan
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
